import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
  useLayoutEffect,
  CSSProperties,
} from "react";
import styled from "styled-components";
import { Typography, Button, Tooltip } from "@mui/material";
import {
  ReactFlow,
  MiniMap,
  Background,
  useNodesState,
  useEdgesState,
  Node,
  Edge,
  Connection,
  Position,
  Handle,
  NodeProps,
  BackgroundVariant,
  MarkerType,
  ReactFlowInstance,
  BaseEdge,
  EdgeProps,
  getBezierPath,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import dagre from "@dagrejs/dagre";
import AddIcon from "@mui/icons-material/Add";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import BusinessIcon from "@mui/icons-material/Business";
import BuildIcon from "@mui/icons-material/Build";
import ListAltIcon from "@mui/icons-material/ListAlt";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ArticleIcon from "@mui/icons-material/Article";
import MonitorIcon from "@mui/icons-material/Monitor";
import WarningIcon from "@mui/icons-material/Warning";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import FitScreenIcon from "@mui/icons-material/FitScreen";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  TestTarget,
  TestTargetFormData,
} from "../types/TestTarget";
import { useToast } from "../contexts/ToastContext";
import { SSTFApiClient } from "../services/api";
import { getTypeIdFromTypeName } from "../services/testTargetService";
import {
  parseParamsSafe,
  toCreatedTestTarget,
} from "../utils/testTargetMapper";
import {
  has_item_transitions,
  normalize_item_transitions,
} from "../utils/itemParams";
import TestTargetDetailView from "./TestTargetDetailView";
import ItemSpecificationPanel from "./ItemSpecificationPanel";
import { ApiConfig } from "../config/apiConfig";
import { hasPendingReviewItems } from "../utils/statusUtils";
import { getViewPaths } from "../utils/viewPathUtils";
import { is_feature_target } from "../utils/viewItemTree";
import { TTM_PAGE_HEADER_HEIGHT_CSS_VAR } from "../constants/uiZIndex";

const EDGE_LABEL_OFFSET_STEP_X = 18;
const EDGE_LABEL_OFFSET_STEP_Y = 14;
const VIEWPORT_MIN_ZOOM = 0.2;
const VIEWPORT_MAX_ZOOM = 1.5;
/** ヘッダー計測前のフォールバック（ResizeObserver 反映までの初期値） */
const DEFAULT_PAGE_HEADER_HEIGHT_PX = 72;
const DEFAULT_FIT_VIEW_OPTIONS = {
  padding: 50,
  minZoom: VIEWPORT_MIN_ZOOM,
  maxZoom: VIEWPORT_MAX_ZOOM,
  includeHiddenNodes: false,
};

// Styled Components
const Container = styled.div`
  flex: 1;
  display: flex;
  height: 100%;
  background-color: #f5f5f5;
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: white;
  position: relative;
`;

const FlowContainer = styled.div`
  flex: 1;
  background: white;
  overflow: hidden;
  position: relative;

  /* エッジの選択時スタイル */
  .react-flow__edge.selected path,
  .react-flow__edge:focus path,
  .react-flow__edge:focus-visible path {
    stroke: #1976d2 !important;
    stroke-width: 4 !important;
    stroke-dasharray: none !important;
  }

  .react-flow__edge.selected .react-flow__edge-text,
  .react-flow__edge:focus .react-flow__edge-text,
  .react-flow__edge:focus-visible .react-flow__edge-text {
    fill: #1976d2 !important;
    font-weight: 700 !important;
  }

  .react-flow__edge.selected .react-flow__edge-textbg,
  .react-flow__edge:focus .react-flow__edge-textbg,
  .react-flow__edge:focus-visible .react-flow__edge-textbg {
    fill: #e3f2fd !important;
    stroke: #1976d2 !important;
    stroke-width: 2 !important;
  }

  /* ホバー時のスタイル */
  .react-flow__edge:hover path {
    stroke-width: 3 !important;
    cursor: pointer;
  }

  .react-flow__edge:hover .react-flow__edge-text {
    font-weight: 600 !important;
  }

  /* リストから選択された画面ノードのハイライト（issue #342 要望2） */
  .react-flow__node.selected {
    outline: 3px solid #1976d2;
    outline-offset: 3px;
    border-radius: 12px;
    transition: outline-color 0.2s ease;
  }

  /*
   * サービス枠（緑枠）のノードラッパーはポインタイベントを透過させ、
   * 枠の本体をドラッグするとビューポートがパンするようにする（issue #342 要望1）。
   * ヘッダー・ドラッグハンドル等の操作部分は子側で pointer-events: auto に戻している。
   */
  .react-flow__node-serviceGroup {
    pointer-events: none !important;
  }
`;

const RightSidebar = styled.div<{
  isOpen: boolean;
  width?: number;
  headerOffsetPx?: number;
}>`
  position: absolute;
  top: ${(props) => `${props.headerOffsetPx ?? 0}px`};
  right: 0;
  height: ${(props) =>
    props.headerOffsetPx != null
      ? `calc(100% - ${props.headerOffsetPx}px)`
      : "100%"};
  width: ${(props) => (props.isOpen ? `${props.width || 450}px` : "0")};
  background: white;
  border-left: 1px solid #e0e0e0;
  box-shadow: ${(props) =>
    props.isOpen ? "-4px 0 20px rgba(0, 0, 0, 0.08)" : "none"};
  transition: ${(props) =>
    props.isOpen
      ? "box-shadow 0.3s ease"
      : "width 0.3s ease, box-shadow 0.3s ease"};
  overflow: hidden;
  z-index: 1250;
  transform: ${(props) =>
    props.isOpen ? "translateX(0)" : "translateX(100%)"};
`;

const ServiceHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  background: white;
  border-bottom: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

const ServiceInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ServiceTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  
  .service-icon {
    color: #667eea;
    display: flex;
    align-items: center;
  }
  
  .service-name {
    font-size: 1.3rem;
  font-weight: 700;
    color: #1e293b;
    margin: 0;
  }
`;

const ServiceStats = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ServiceActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

// ヘッダー用のスタイルコンポーネント
const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  background: white;
  border-bottom: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  z-index: 10;
`;

const PageHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
`;

const PageHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #f1f5f9;
  border-radius: 20px;
  font-size: 0.8rem;
  color: #475569;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
  white-space: nowrap; /* テキストの折り返しを防止 */
  flex-shrink: 0; /* 縮小を防止 */
  
  &:hover {
    background: #e2e8f0;
    border-color: #cbd5e1;
  }
  
  .stat-icon {
    color: #667eea;
    display: flex;
    align-items: center;
  }
  
  .stat-number {
    font-weight: 700;
    color: #1e293b;
    font-size: 0.9rem;
  }
  
  .stat-label {
    color: #64748b;
    font-weight: 500;
  }
`;

const ActionButton = styled(Button)`
  background: #667eea !important;
  color: white !important;
  text-transform: none !important;
  border-radius: 8px !important;
  padding: 8px 16px !important;
  font-weight: 600 !important;
  box-shadow: 0 2px 4px rgba(102, 126, 234, 0.2) !important;
  white-space: nowrap !important; /* テキストの折り返しを防止 */
  flex-shrink: 0 !important; /* 縮小を防止 */

  &:hover {
    background: #5a67d8 !important;
    box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3) !important;
    transform: translateY(-1px);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

// カスタムコントロールパネルのスタイル
const CustomControlsPanel = styled.div`
  position: absolute;
  bottom: 20px;
  left: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 4;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 4px;
`;

const ControlButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s ease;
  padding: 0;

  &:hover:not(:disabled) {
    background: #f1f5f9;
    color: #1e293b;
  }

  &:active:not(:disabled) {
    background: #e2e8f0;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #9e9e9e;
  padding: 60px 24px;
`;

// Serviceグループノード用のスタイル
const ServiceGroupContainer = styled.div<{
  serviceColor: string;
  width: number;
  height: number;
}>`
  padding: 24px;
  border-radius: 12px;
  background: ${(props) => props.serviceColor}15;
  border: 3px solid ${(props) => props.serviceColor};
  width: ${(props) => props.width}px;
  height: ${(props) => props.height}px;
  min-width: 1250px; /* 統計項目とボタンが折り返されない最小幅を設定 */
  min-height: 200px; /* Service名（2行）+ ヘッダー高さ + パディングを考慮した最小高さ */
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  position: relative;
  box-sizing: border-box;
  pointer-events: none; /* Viewノードのクリックを可能にするため、背景部分はクリックイベントを無効化 */
  z-index: 1; /* Viewノードより下に配置 */
  user-select: none; /* テキスト選択を無効化 */
`;

const ServiceGroupHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid rgba(255, 255, 255, 0.3);
  pointer-events: auto; /* ヘッダー部分はクリック可能 */
  z-index: 2; /* Viewノードより上に配置 */
  flex-wrap: nowrap; /* 要素の折り返しを防止 */
  gap: 12px; /* 要素間の最小間隔を確保 */
`;

// Serviceグループ移動用の専用ドラッグハンドル。
// これ以外の場所をドラッグした場合はノードではなくビューポートがパンされる（issue #342 要望1）。
const ServiceGroupDragHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  margin-right: 4px;
  border-radius: 6px;
  color: #64748b;
  cursor: grab;
  pointer-events: auto; /* ハンドルだけドラッグ可能にする */
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: rgba(100, 116, 139, 0.12);
    color: #1e293b;
  }

  &:active {
    cursor: grabbing;
  }
`;

const ServiceGroupTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #1e293b;
  font-size: 1.2rem;
  font-weight: 700;
  flex-shrink: 0;
  
  span {
    /* Service名を30文字×2行に制限 */
    max-width: 30ch; /* 30文字分の幅（1ch ≈ 1文字） */
    max-height: 2.8em; /* 2行分の高さ（行間1.4を考慮） */
    line-height: 1.4;
    word-wrap: break-word;
    overflow-wrap: break-word;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2; /* 2行に制限 */
    -webkit-box-orient: vertical;
  }
`;

const ServiceGroupStats = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-shrink: 0; /* 縮小を防止 */
`;

const ServiceGroupActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0; /* 縮小を防止 */
`;

const NodeContainer = styled.div<{
  type: "service" | "view" | "component" | "item";
  featureColor?: string;
  isChildItem?: boolean;
  needsReview?: boolean;
  isEntryPoint?: boolean;
}>`
  padding: 16px;
  border-radius: 10px;
    background: ${(props) => {
      if (
        (props.type === "view" || props.type === "component") &&
        props.featureColor
      ) {
        // 機能別の色を使用してグラデーションを作成
        const color = props.featureColor;
        return `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`;
      }
      switch (props.type) {
        case "service":
          return "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";
        case "view":
          return "linear-gradient(135deg, #4caf50 0%, #388e3c 100%)";
        case "component":
          return "linear-gradient(135deg, #5c6bc0 0%, #3949ab 100%)";
        case "item":
          return "linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)";
        default:
          return "#ffffff";
      }
    }};
  color: white;
  min-width: ${(props) => (props.isChildItem ? "120px" : "150px")};
  max-width: ${(props) => (props.isChildItem ? "200px" : "280px")};
  min-height: ${(props) => (props.isChildItem ? "60px" : "80px")};
  width: auto;
  height: auto;
  border: ${(props) => {
    if (props.isEntryPoint) return "3px solid #00BFA5";
    if (props.needsReview) return "3px solid #FF9800";
    return "2px solid rgba(255, 255, 255, 0.2)";
  }};
  box-shadow: ${(props) => {
    if (props.isEntryPoint && props.needsReview) {
      return "0 0 12px rgba(0, 191, 165, 0.5), 0 0 12px rgba(255, 152, 0, 0.5), 0 4px 16px rgba(0, 0, 0, 0.1)";
    }
    if (props.isEntryPoint) {
      return "0 0 12px rgba(0, 191, 165, 0.5), 0 4px 16px rgba(0, 0, 0, 0.1)";
    }
    if (props.needsReview) {
      return "0 0 12px rgba(255, 152, 0, 0.5), 0 4px 16px rgba(0, 0, 0, 0.1)";
    }
    return "0 4px 16px rgba(0, 0, 0, 0.1)";
  }};
  position: relative;
  z-index: 10; /* Serviceグループノードより上に配置 */
  transition: all 0.3s ease;
  cursor: pointer;
  opacity: ${(props) => (props.isChildItem ? "0.9" : "1")};
  transform: ${(props) => (props.isChildItem ? "scale(0.85)" : "scale(1)")};

  &:hover {
    transform: ${(props) =>
      props.isChildItem ? "scale(0.9) translateY(-2px)" : "translateY(-4px)"};
    box-shadow: ${(props) => {
      if (props.isEntryPoint) {
        return "0 0 16px rgba(0, 191, 165, 0.6), 0 8px 24px rgba(0, 0, 0, 0.2)";
      }
      if (props.needsReview) {
        return "0 0 16px rgba(255, 152, 0, 0.6), 0 8px 24px rgba(0, 0, 0, 0.2)";
      }
      return "0 8px 24px rgba(0, 0, 0, 0.2)";
    }};
  }

  /* レビュー待ちバッジ */
  &::before {
    content: ${(props) => (props.needsReview ? '"⚠"' : '""')};
    display: ${(props) => (props.needsReview ? "flex" : "none")};
    position: absolute;
    top: -8px;
    right: -8px;
    width: 24px;
    height: 24px;
    background: #FF9800;
    border-radius: 50%;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    box-shadow: 0 2px 8px rgba(255, 152, 0, 0.4);
    z-index: 10;
  }

  .node-header {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
  }

  .node-icon {
    margin-right: 8px;
    font-size: 1.2rem;
  }

  .node-title {
    flex: 1;
    font-weight: 600;
    font-size: 0.9rem;
    line-height: 1.4;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }



`;

// カスタムノードコンポーネント
const CustomNode: React.FC<NodeProps> = ({ data, id }) => {
  const {
    target,
    onViewItems,
    allTargets,
    featureColor,
    isChildItem,
  } = data as {
    target: TestTarget & { displayName?: string };
    allTargets: TestTarget[];
    featureColor?: string;
    isChildItem?: boolean;
    onViewItems?: (target: TestTarget) => void;
  };

  const getIcon = (type: string) => {
    switch (type) {
      case "service":
        return <BusinessIcon sx={{ fontSize: 20 }} />;
      case "view":
        return <MonitorIcon sx={{ fontSize: 20 }} />;
      case "component":
        return <ViewModuleIcon sx={{ fontSize: 20 }} />;
      case "item":
        return <ArticleIcon sx={{ fontSize: 20 }} />;
      default:
        return <ArticleIcon sx={{ fontSize: 20 }} />;
    }
  };

  const getChildItems = (target: TestTarget, allTargets: TestTarget[]) => {
    if (target.type === "view" || target.type === "component") {
      return allTargets.filter(
        (t) =>
          t.parentId === target.id &&
          !is_feature_target(t) &&
          (t.type === "item" || t.type === "section" || t.type === "overlay")
      );
    }
    return [];
  };

  const items = getChildItems(target, allTargets || []);

  // レビュー待ち状態チェック（下書き、レビュー待ち、レビュー中）
  // View配下の全ての子ターゲット、ユースケース、ポリシー、遷移をチェック
  const pendingReviewInfo = hasPendingReviewItems(target, allTargets);
  const needsReview =
    pendingReviewInfo.target ||
    pendingReviewInfo.usecases.length > 0 ||
    pendingReviewInfo.policies.length > 0 ||
    pendingReviewInfo.transitions.length > 0 ||
    pendingReviewInfo.childTargets.length > 0;

  // Viewのパス未設定チェック
  const hasPathWarning = (() => {
    if (target.type !== "view") return false;
    const paths = getViewPaths(target.params);
    return !(paths.length > 0 && paths.some((p) => p.path && p.path.trim() !== ""));
  })();

  // エントリーポイントチェック
  const isEntryPoint = (() => {
    if (target.type !== "view") return false;
    if (target.params && typeof target.params === "object" && "entry_point" in target.params) {
      return Boolean(target.params.entry_point);
    }
    return false;
  })();

  // ノードクリック時の処理
  const handleNodeClick = () => {
    if (target.type === "view" || target.type === "component") {
      onViewItems?.(target);
    }
  };

  // Viewノードの表示（機能別の色分けあり）
  return (
    <NodeContainer
      type={target.type as any}
      featureColor={featureColor}
      isChildItem={isChildItem}
      needsReview={needsReview}
      isEntryPoint={isEntryPoint}
      onClick={handleNodeClick}
    >
      <Handle type="target" position={Position.Top} />
      <Handle type="source" position={Position.Bottom} />

      {/* エントリーポイントバッジ（左上に配置） */}
      {isEntryPoint && (
        <Tooltip title="エントリーポイント" arrow>
          <div
            style={{
              position: "absolute",
              top: "-8px",
              left: "-8px",
              width: "24px",
              height: "24px",
              background: "#00BFA5",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 2px 8px rgba(0, 191, 165, 0.4)",
              zIndex: 10,
            }}
          >
            <PlayArrowIcon sx={{ fontSize: 14, color: "white" }} />
          </div>
        </Tooltip>
      )}

      {/* パス未設定警告アイコン（needsReviewバッジと同時に表示可能） */}
      {hasPathWarning && (
        <Tooltip title="パスが設定されていません" arrow>
          <div
            style={{
              position: "absolute",
              top: "-8px",
              right: needsReview ? "20px" : "-8px", // needsReviewがある場合は左側に配置
              width: "24px",
              height: "24px",
              background: "#F44336",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 2px 8px rgba(244, 67, 54, 0.4)",
              zIndex: 10,
            }}
          >
            <WarningIcon sx={{ fontSize: 14, color: "white" }} />
          </div>
        </Tooltip>
      )}

      <div className="node-header">
        <div className="node-icon">{getIcon(target.type)}</div>
        <div
          className="node-title"
          style={
            target.type === "view" || target.type === "component"
              ? {
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical" as const,
                }
              : undefined
          }
        >
          {/* displayNameを使用（機能名 - 画面名の形式） */}
          {target.displayName || target.name}
        </div>
        {(target.type === "view" || target.type === "component") &&
          items.length > 0 && (
          <div
            style={{
              background: "rgba(255, 255, 255, 0.3)",
              borderRadius: "12px",
              padding: "2px 8px",
              fontSize: "0.7rem",
              fontWeight: "600",
              marginLeft: "8px",
              minWidth: "20px",
              textAlign: "center",
            }}
          >
            {items.length}
          </div>
        )}
      </div>

      {target.description &&
        (target.type === "view" || target.type === "component") && (
        <div
          style={{
            fontSize: "0.7rem",
            opacity: 0.8,
            marginBottom: "6px",
            lineHeight: 1.3,
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 4,
            WebkitBoxOrient: "vertical" as const,
          }}
        >
          {target.description}
        </div>
      )}
    </NodeContainer>
  );
};

// Serviceグループノードコンポーネント
const ServiceGroupNode: React.FC<NodeProps> = ({ data }) => {
  const {
    service,
    testTargets,
    onAddChild,
    onOpenServiceDetail,
    viewCount,
    itemCount,
    transitionCount,
    entryPointCount,
    serviceColor,
    width,
    height,
  } = data as {
    service: TestTarget;
    testTargets: TestTarget[];
    onAddChild?: (parentId: string, type: "view" | "item") => void;
    onOpenServiceDetail?: (service: TestTarget) => void;
    viewCount: number;
    itemCount: number;
    transitionCount: number;
    entryPointCount: number;
    serviceColor: string;
    width?: number;
    height?: number;
  };

  // Serviceグループノードのクリックイベントを処理（選択を防ぐ）
  const handleContainerClick = (e: React.MouseEvent) => {
    // ヘッダー部分のクリックは許可（ボタンなどが動作するように）
    // 背景部分のクリックは無視（Viewノードのクリックを可能にするため）
    if (e.target === e.currentTarget) {
      e.stopPropagation();
    }
  };

  return (
    <ServiceGroupContainer 
      serviceColor={serviceColor}
      width={width || 1250} /* デフォルト幅を1250pxに変更。UIが崩れないようにするため。 */
      height={height || 200} /* デフォルト高さを200pxに変更。UIが崩れないようにするため。 */
      onClick={handleContainerClick}
    >
      <ServiceGroupHeader>
        <ServiceGroupTitle>
          <Tooltip title="ドラッグでサービスを移動" arrow placement="top">
            <ServiceGroupDragHandle className="service-group-drag-handle">
              <DragIndicatorIcon sx={{ fontSize: 20 }} />
            </ServiceGroupDragHandle>
          </Tooltip>
          <BusinessIcon sx={{ fontSize: 24, color: serviceColor }} />
          <Tooltip title={onOpenServiceDetail ? "クリックで詳細を開く" : service.name} arrow placement="top">
            <span
              onClick={(e) => {
                if (onOpenServiceDetail) {
                  e.stopPropagation();
                  onOpenServiceDetail(service);
                }
              }}
              style={{
                cursor: onOpenServiceDetail ? "pointer" : undefined,
              }}
            >
              {service.name}
            </span>
          </Tooltip>
        </ServiceGroupTitle>
        <ServiceGroupStats>
          <Tooltip
            title={entryPointCount === 0 ? "エントリーポイントが設定されていません" : "エントリーポイント数"}
            arrow
            placement="top"
          >
            <StatItem
              style={entryPointCount === 0 ? {
                background: "rgba(255, 152, 0, 0.15)",
                borderRadius: "6px",
                padding: "4px 8px",
                margin: "-4px 0",
              } : {}}
            >
              <div
                className="stat-icon"
                style={{ color: entryPointCount === 0 ? "#FF9800" : "#00BFA5" }}
              >
                <PlayArrowIcon sx={{ fontSize: 16 }} />
              </div>
              <span
                className="stat-number"
                style={{ color: entryPointCount === 0 ? "#FF9800" : "#00BFA5" }}
              >
                {entryPointCount}
              </span>
              <span
                className="stat-label"
                style={{ color: entryPointCount === 0 ? "#FF9800" : undefined }}
              >
                EP
              </span>
              {entryPointCount === 0 && (
                <WarningIcon sx={{ fontSize: 14, color: "#FF9800", marginLeft: "2px" }} />
              )}
            </StatItem>
          </Tooltip>
          <StatItem>
            <div className="stat-icon">
              <BuildIcon sx={{ fontSize: 16 }} />
            </div>
            <span className="stat-number">{viewCount}</span>
            <span className="stat-label">ビュー</span>
          </StatItem>
          <StatItem>
            <div className="stat-icon">
              <ListAltIcon sx={{ fontSize: 16 }} />
            </div>
            <span className="stat-number">{itemCount}</span>
            <span className="stat-label">アイテム</span>
          </StatItem>
          <StatItem>
            <div className="stat-icon">
              <SwapHorizIcon sx={{ fontSize: 16 }} />
            </div>
            <span className="stat-number">{transitionCount}</span>
            <span className="stat-label">遷移</span>
          </StatItem>
        </ServiceGroupStats>
        <ServiceGroupActions>
          <ActionButton
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => onAddChild?.(service.id, "view")}
          >
            ビューを追加
          </ActionButton>
        </ServiceGroupActions>
      </ServiceGroupHeader>
      {/* ViewノードはこのServiceグループノードの子として配置される */}
    </ServiceGroupContainer>
  );
};

const nodeTypes = {
  custom: CustomNode,
  serviceGroup: ServiceGroupNode,
};

// カスタムエッジコンポーネント（ラベルドラッグ対応）
const CustomDraggableBezierEdge: React.FC<
  EdgeProps & {
    onControlPointChange?: (
      edgeId: string,
      controlPoint: { x: number; y: number }
    ) => void;
    screenToFlowCoordinate?: (
      point: { x: number; y: number }
    ) => { x: number; y: number };
    onOpenItemDetail?: (itemId: string) => void; // Item詳細を開くコールバック
  }
> = (props) => {
  const {
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    style,
    markerEnd,
    label,
    labelStyle,
    labelBgStyle,
    id,
    onControlPointChange,
    screenToFlowCoordinate,
    onOpenItemDetail,
  } = props;

  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({
    x: 0,
    y: 0,
    controlX: 0,
    controlY: 0,
  });

  // 保存された制御点を取得（なければデフォルトのベジェ曲線を使用）
  const savedControlPoint = (props.data as any)?.controlPoint;

  let edgePath: string;
  let labelX: number;
  let labelY: number;
  let controlX: number;
  let controlY: number;

  // ベジェ曲線の中点を計算する関数（t=0.5）
  const calculateBezierMidpoint = (
    p0: { x: number; y: number },
    p1: { x: number; y: number },
    p2: { x: number; y: number },
    p3: { x: number; y: number },
    t: number = 0.5
  ) => {
    const mt = 1 - t;
    const mt2 = mt * mt;
    const mt3 = mt2 * mt;
    const t2 = t * t;
    const t3 = t2 * t;

    return {
      x: mt3 * p0.x + 3 * mt2 * t * p1.x + 3 * mt * t2 * p2.x + t3 * p3.x,
      y: mt3 * p0.y + 3 * mt2 * t * p1.y + 3 * mt * t2 * p2.y + t3 * p3.y,
    };
  };

  // テキストの幅を計算する関数（全角・半角を考慮）
  const calculateTextWidth = (text: string, fontSize: number = 12): number => {
    if (typeof text !== "string") return 10 * fontSize * 0.6;
    
    let width = 0;
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      // 全角文字（日本語、中国語、韓国語など）の判定
      // 文字コードが0x00-0x7Fの範囲外、または全角記号・数字・英字
      const charCode = char.charCodeAt(0);
      if (
        charCode > 0x7F ||
        (charCode >= 0xFF00 && charCode <= 0xFFEF) || // 全角英数字・記号
        (charCode >= 0x3040 && charCode <= 0x309F) || // ひらがな
        (charCode >= 0x30A0 && charCode <= 0x30FF) || // カタカナ
        (charCode >= 0x4E00 && charCode <= 0x9FAF) // CJK統合漢字
      ) {
        width += fontSize; // 全角文字はフォントサイズと同じ幅
      } else {
        width += fontSize * 0.6; // 半角文字はフォントサイズの0.6倍
      }
    }
    return width;
  };

  if (savedControlPoint) {
    // カスタム制御点を使用（相対座標として保存されているため絶対座標に変換）
    // 始点と終点の中点を計算
    const midpointX = (sourceX + targetX) / 2;
    const midpointY = (sourceY + targetY) / 2;
    
    // 相対座標を絶対座標に変換（中点に相対位置を加算）
    controlX = midpointX + savedControlPoint.x;
    controlY = midpointY + savedControlPoint.y;

    // ベジェ曲線のパスを生成（M x1 y1 C cx1 cy1, cx2 cy2, x2 y2）
    // 始点と終点は固定、制御点はドラッグ可能な中間点を使用
    const cx1 = controlX;
    const cy1 = controlY;
    const cx2 = controlX;
    const cy2 = controlY;
    edgePath = `M ${sourceX} ${sourceY} C ${cx1} ${cy1}, ${cx2} ${cy2}, ${targetX} ${targetY}`;

    // ラベル位置はベジェ曲線の中点（t=0.5）を計算
    const midpoint = calculateBezierMidpoint(
      { x: sourceX, y: sourceY },
      { x: cx1, y: cy1 },
      { x: cx2, y: cy2 },
      { x: targetX, y: targetY },
      0.5
    );
    labelX = midpoint.x;
    labelY = midpoint.y;
  } else {
    // デフォルトのベジェ曲線を使用
    const [path, lx, ly] = getBezierPath({
      sourceX,
      sourceY,
      sourcePosition: sourcePosition || Position.Bottom,
      targetX,
      targetY,
      targetPosition: targetPosition || Position.Top,
    });
    edgePath = path;
    labelX = lx;
    labelY = ly;

    // デフォルトの中間点を計算（ベジェ曲線の中間点を近似）
    controlX = (sourceX + targetX) / 2;
    controlY = (sourceY + targetY) / 2;
  }

  const labelOffsetIndex =
    typeof (props.data as any)?.labelOffsetIndex === "number"
      ? (props.data as any).labelOffsetIndex
      : 0;

  if (labelOffsetIndex !== 0) {
    labelX += labelOffsetIndex * EDGE_LABEL_OFFSET_STEP_X;
    labelY += labelOffsetIndex * -EDGE_LABEL_OFFSET_STEP_Y;
  }

  // ラベル部分のドラッグ開始
  const handleLabelMouseDown = (e: React.MouseEvent<SVGRectElement>) => {
    e.stopPropagation();
    e.preventDefault(); // エッジの選択を防ぐ
    
    // ドラッグ開始位置を記録
    const startX = e.clientX;
    const startY = e.clientY;
    
    setIsDragging(true);
    setDragStart({
      x: startX,
      y: startY,
      controlX,
      controlY,
    });
  };

  // ドラッグ処理
  useEffect(() => {
    if (!isDragging) return;

    let hasMoved = false; // マウスが移動したかどうかを追跡

    const handleMouseMove = (e: MouseEvent) => {
      // マウスの移動量を計算
      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;
      
      // 移動量が小さい場合はドラッグとして扱わない（クリックと区別）
      const moveThreshold = 3; // ピクセル単位の閾値
      if (Math.abs(deltaX) < moveThreshold && Math.abs(deltaY) < moveThreshold) {
        return;
      }
      
      hasMoved = true;
      e.preventDefault(); // エッジの選択を防ぐ
      e.stopPropagation();

      // 座標変換関数を使用してスクリーン座標をReactFlow座標に変換
      let flowDeltaX = deltaX;
      let flowDeltaY = deltaY;

      if (screenToFlowCoordinate) {
        const startPoint = screenToFlowCoordinate({
          x: dragStart.x,
          y: dragStart.y,
        });
        const currentPoint = screenToFlowCoordinate({
          x: e.clientX,
          y: e.clientY,
        });
        flowDeltaX = currentPoint.x - startPoint.x;
        flowDeltaY = currentPoint.y - startPoint.y;
      } else {
        // 座標変換関数がない場合は、簡易的な変換（ズームを考慮）
        // 実際のズームレベルを取得する必要があるが、ここでは1.0を仮定
        const zoom = 1.0;
        flowDeltaX = deltaX / zoom;
        flowDeltaY = deltaY / zoom;
      }

      // 新しい制御点の絶対座標を計算
      const newControlPointAbsolute = {
        x: dragStart.controlX + flowDeltaX,
        y: dragStart.controlY + flowDeltaY,
      };
      
      // 始点と終点の中点を計算（現在の位置を使用）
      const midpointX = (sourceX + targetX) / 2;
      const midpointY = (sourceY + targetY) / 2;
      
      // 相対座標に変換（中点からの相対位置）
      const newControlPoint = {
        x: newControlPointAbsolute.x - midpointX,
        y: newControlPointAbsolute.y - midpointY,
      };

      // 親コンポーネントに制御点変更を通知（相対座標として保存）
      if (onControlPointChange && id) {
        onControlPointChange(id, newControlPoint);
      }
    };

    const handleMouseUp = (e: MouseEvent) => {
      // ドラッグが開始されていて、移動がなかった場合はクリックとして扱う
      // この場合、エッジの選択を許可する
      if (!hasMoved) {
        // クリックイベントを再発火させてエッジの選択を許可
        // ただし、これは複雑になるので、ここでは単にドラッグを終了するだけ
      }
      
      setIsDragging(false);
      hasMoved = false;
    };

    // ドラッグ中はエッジの選択を防ぐため、mousedownイベントをキャプチャ
    const handleMouseDownCapture = (e: MouseEvent) => {
      if (isDragging) {
        e.stopPropagation();
      }
    };

    window.addEventListener("mousemove", handleMouseMove, { passive: false });
    window.addEventListener("mouseup", handleMouseUp);
    window.addEventListener("mousedown", handleMouseDownCapture, true);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("mousedown", handleMouseDownCapture, true);
    };
  }, [
    isDragging,
    dragStart,
    onControlPointChange,
    id,
    screenToFlowCoordinate,
    controlX,
    controlY,
  ]);

  // エッジのスタイルにpointer-eventsを追加
  // NOTE: エッジのパス部分はpointer-events: noneにして下層のクリックを妨げないようにする
  // ラベル部分はpointer-events: allでクリック可能にする
  const edgeStyleWithPointerEvents = {
    ...style,
    pointerEvents: "none" as const, // エッジのパス部分はクリックイベントをブロックしない
  };

  return (
    <>
      <BaseEdge 
        path={edgePath} 
        style={edgeStyleWithPointerEvents} 
        markerEnd={markerEnd}
      />
      {label && (() => {
        const labelText = typeof label === "string" ? label : String(label);
        const fontSize = labelStyle?.fontSize 
          ? parseFloat(String(labelStyle.fontSize).replace("px", "")) 
          : 12;
        const textWidth = calculateTextWidth(labelText, fontSize);
        const padding = 8; // 左右のパディング
        const rectWidth = textWidth + padding * 2;
        const rectHeight = 16;
        
        return (
          <g style={{ pointerEvents: "all" }}>
            <rect
              x={labelX - rectWidth / 2}
              y={labelY - rectHeight / 2}
              width={rectWidth}
              height={rectHeight}
              fill={labelBgStyle?.fill || "white"}
              fillOpacity={labelBgStyle?.fillOpacity || 0.8}
              stroke={labelBgStyle?.stroke}
              strokeWidth={labelBgStyle?.strokeWidth || 1}
              rx={4}
              style={{ cursor: "move" }}
              pointerEvents="all"
              onMouseDown={handleLabelMouseDown}
              onClick={(e) => {
                // ドラッグ中でない場合のみクリックイベントを伝播
                if (!isDragging) {
                  e.stopPropagation();
                }
              }}
              onDoubleClick={(e) => {
                // ドラッグ中でない場合のみダブルクリックを処理
                if (!isDragging && onOpenItemDetail) {
                  e.stopPropagation();
                  const itemId = (props.data as any)?.itemId;
                  if (itemId) {
                    onOpenItemDetail(itemId);
                  }
                }
              }}
            />
            <text
              x={labelX}
              y={labelY}
              textAnchor="middle"
              dominantBaseline="middle"
              fill={labelStyle?.fill || "#333"}
              fontSize={fontSize}
              fontWeight={labelStyle?.fontWeight || 500}
              style={labelStyle}
              pointerEvents="none"
            >
              {label}
            </text>
          </g>
        );
      })()}
    </>
  );
};

// 自動レイアウト関数
// ノード位置の重なりを解消する関数
const adjustNodePositions = (nodes: Node[], direction = "TB") => {
  const adjusted = [...nodes];
  const positionMap = new Map<string, Node[]>();

  // 同じ座標のノードをグループ化
  adjusted.forEach((node) => {
    const key = `${Math.round(node.position.x / 20) * 20},${
      Math.round(node.position.y / 20) * 20
    }`;
    if (!positionMap.has(key)) {
      positionMap.set(key, []);
    }
    positionMap.get(key)!.push(node);
  });

  // 重なっているノードを微調整
  positionMap.forEach((groupNodes) => {
    if (groupNodes.length > 1) {
      groupNodes.forEach((node, index) => {
        const offsetMultiplier = Math.floor(index / 2) + 1;
        const isEven = index % 2 === 0;

        if (direction === "TB") {
          // 縦配置の場合、左右にずらす
          node.position.x += (isEven ? 1 : -1) * offsetMultiplier * 25;
          node.position.y += index * 10; // 少し縦もずらす
        } else {
          // 横配置の場合、上下にずらす
          node.position.y += (isEven ? 1 : -1) * offsetMultiplier * 25;
          node.position.x += index * 10; // 少し横もずらす
        }
      });
    }
  });

  return adjusted;
};

// エッジの重なりを検出して調整する関数
const adjustEdgeStyles = (edges: Edge[], nodes: Node[]) => {
  const edgeGroups = new Map<string, Edge[]>();

  // 類似の方向を持つエッジをグループ化
  edges.forEach((edge) => {
    const sourceNode = nodes.find((n) => n.id === edge.source);
    const targetNode = nodes.find((n) => n.id === edge.target);

    if (sourceNode && targetNode) {
      const deltaX = targetNode.position.x - sourceNode.position.x;
      const deltaY = targetNode.position.y - sourceNode.position.y;
      const angle = Math.atan2(deltaY, deltaX);
      const roundedAngle = (Math.round((angle * 4) / Math.PI) * Math.PI) / 4; // 45度単位で丸める

      const groupKey = `${roundedAngle}`;
      if (!edgeGroups.has(groupKey)) {
        edgeGroups.set(groupKey, []);
      }
      edgeGroups.get(groupKey)!.push(edge);
    }
  });

  // 同じ方向のエッジにスタイル調整を追加
  edgeGroups.forEach((groupEdges) => {
    if (groupEdges.length > 1) {
      groupEdges.forEach((edge, index) => {
        // カスタムエッジタイプの場合は変更しない（customDraggableBezierを保持）
        // デフォルトのbezierタイプの場合のみ変更する必要があるが、
        // 現在はすべてのエッジがcustomDraggableBezierなので変更しない

        const style = (edge.style ?? {}) as CSSProperties;
        const strokeColorForLabel =
          typeof style.stroke === "string" ? style.stroke : "#333";
        const strokeColorForLabelBg =
          typeof style.stroke === "string" ? style.stroke : "#757575";

        // ラベルスタイルを改善（位置は変更しない）
        edge.labelStyle = {
          ...edge.labelStyle,
          fontSize: "10px",
          fontWeight: 600,
          fill: strokeColorForLabel,
        };

        // ラベル背景を見やすく調整
        edge.labelBgStyle = {
          ...edge.labelBgStyle,
          fillOpacity: 0.9,
          fill: "white",
          stroke: strokeColorForLabelBg,
          strokeWidth: 1,
        };

        // 重なりエッジの透明度と太さを調整
        if (edge.style && index > 0) {
          const strokeWidthValue = style.strokeWidth;
          const numericStrokeWidth =
            typeof strokeWidthValue === "number"
              ? strokeWidthValue
              : strokeWidthValue
              ? Number.parseFloat(strokeWidthValue as string)
              : Number.NaN;
          const currentStrokeWidth = Number.isFinite(numericStrokeWidth)
            ? numericStrokeWidth
            : 2;

          edge.style = {
            ...style,
            opacity: 0.85,
            strokeWidth: Math.max(currentStrokeWidth - 0.5, 1),
          };
        }
      });
    }
  });

  return edges;
};

const getLayoutedElements = (
  nodes: Node[],
  edges: Edge[],
  direction = "TB"
) => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  // 実際のノードサイズに合わせて調整
  const avgNodeWidth = 280;
  const avgNodeHeight = 150;

  dagreGraph.setGraph({
    rankdir: direction,
    nodesep: 140, // ノード間隔をさらに増加
    ranksep: 200, // ランク間隔をさらに増加
    align: "UL",
    marginx: 60,
    marginy: 60,
  });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: avgNodeWidth, height: avgNodeHeight });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  nodes.forEach((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);

    // 方向に応じてハンドルの位置を設定
    if (direction === "TB") {
      node.targetPosition = Position.Top;
      node.sourcePosition = Position.Bottom;
    } else {
      node.targetPosition = Position.Left;
      node.sourcePosition = Position.Right;
    }

    // ノード位置の調整
    node.position = {
      x: nodeWithPosition.x - avgNodeWidth / 2,
      y: nodeWithPosition.y - avgNodeHeight / 2,
    };

    return node;
  });

  // ノード位置の重なりを解消
  const adjustedNodes = adjustNodePositions(nodes, direction);

  // エッジの重なりを調整
  const adjustedEdges = adjustEdgeStyles(edges, adjustedNodes);

  return { nodes: adjustedNodes, edges: adjustedEdges };
};

interface TestTargetGraphViewProps {
  services: TestTarget[];  // 全Service
  selectedServiceIds: Set<string>;  // 表示するServiceのIDセット
  onSelectedServiceIdsChange: (ids: Set<string>) => void;
  testTargets: TestTarget[];
  testcaseMappings?: import("../services/api").TestcaseMappings | null;
  loading?: boolean;
  onRefresh: () => void;
  onUpdateTestTarget?: (updatedTarget: TestTarget) => void;
  onAddTestTarget?: (newTarget: TestTarget) => void;
  onMoveTarget: (targetId: string, newParentId: string) => void;
  onAddChild: (parentId: string, type: "view" | "item") => void;
  onCreateNew?: () => void;
  onDeleteTarget?: (
    targetId: string,
    options?: { suppressSuccess?: boolean }
  ) => Promise<void>;
  apiClient?: SSTFApiClient | null;
  apiConfig?: ApiConfig;
  workspaceId?: string;
  projectId?: string;
  workspaceName?: string;  // ワークスペース名
  projectName?: string;  // プロジェクト名
  selectedViewForItems?: TestTarget | null;
  // View 選択の発生源。"sidebar" のときだけ全体ビューをパン・ハイライトする（issue #342 要望2）。
  selectedViewSource?: "graph" | "panel" | "sidebar" | null;
  onViewPanelClose?: () => void; // 項目仕様書パネルを閉じたときに呼ばれるコールバック
  // View を選択したときに呼ばれるコールバック。source で発生源を区別する（既定はサイドバー）。
  onViewSelect?: (
    view: TestTarget,
    source?: "graph" | "panel" | "sidebar"
  ) => void;
  operationLogService?: import("../services/operationLogService").OperationLogService;
  onOpenItemDetail?: (itemId: string) => void; // Item詳細を開くコールバック
  // ServiceListからService詳細を開くリクエスト（親が設定し、開いたら onOpenServiceDetailHandled でクリア）
  requestedServiceForDetail?: TestTarget | null;
  onOpenServiceDetailHandled?: () => void;
  // URLパラメータによるダイアログ制御
  urlTargetId?: string;
  urlUsecaseId?: string;
  urlEditMode?: boolean;
  onCloseDialog?: () => void;
}

const TestTargetGraphView: React.FC<TestTargetGraphViewProps> = ({
  services,
  selectedServiceIds,
  onSelectedServiceIdsChange,
  testTargets,
  testcaseMappings,
  loading = false,
  onRefresh,
  onUpdateTestTarget,
  onAddTestTarget,
  onMoveTarget,
  onAddChild,
  onCreateNew,
  onDeleteTarget,
  apiClient,
  apiConfig,
  workspaceId,
  projectId,
  workspaceName,
  projectName,
  selectedViewForItems,
  selectedViewSource,
  onViewPanelClose,
  onViewSelect,
  operationLogService,
  onOpenItemDetail,
  requestedServiceForDetail,
  onOpenServiceDetailHandled,
  urlTargetId,
  urlEditMode = false,
  onCloseDialog,
}) => {
  const { showError } = useToast();

  const [detailViewOpen, setDetailViewOpen] = useState(false);
  const [selectedTarget, setSelectedTarget] = useState<TestTarget | null>(null);
  const [detailViewMode, setDetailViewMode] = useState<
    "view" | "edit" | "create"
  >("view");
  const [itemsDialogOpen, setItemsDialogOpen] = useState(false);
  const [selectedViewTarget, setSelectedViewTarget] =
    useState<TestTarget | null>(null);
  // 「参照先を開く」で切り替える前のパネル対象を積んでおき、戻る導線で復帰する
  const [referenceNavStack, setReferenceNavStack] = useState<TestTarget[]>([]);
  const [peekViewWidth, setPeekViewWidth] = useState(450);

  const [detailViewInitialTab, setDetailViewInitialTab] = useState<string>("basic");

  const pageHeaderResizeObserverRef = useRef<ResizeObserver | null>(null);
  const [pageHeaderHeightPx, setPageHeaderHeightPx] = useState(
    DEFAULT_PAGE_HEADER_HEIGHT_PX
  );

  const attachPageHeaderMeasurements = useCallback(
    (headerEl: HTMLDivElement | null) => {
      pageHeaderResizeObserverRef.current?.disconnect();
      pageHeaderResizeObserverRef.current = null;

      if (!headerEl) {
        document.documentElement.style.removeProperty(
          TTM_PAGE_HEADER_HEIGHT_CSS_VAR
        );
        return;
      }

      const syncHeight = () => {
        const next = Math.ceil(headerEl.getBoundingClientRect().height);
        if (next > 0) {
          setPageHeaderHeightPx(next);
          document.documentElement.style.setProperty(
            TTM_PAGE_HEADER_HEIGHT_CSS_VAR,
            `${next}px`
          );
        }
      };

      syncHeight();
      if (typeof ResizeObserver === "undefined") {
        return;
      }
      const observer = new ResizeObserver(syncHeight);
      pageHeaderResizeObserverRef.current = observer;
      observer.observe(headerEl);
    },
    []
  );

  // Item詳細を開くハンドラー
  const handleOpenItemDetail = useCallback((itemId: string) => {
    // itemIdでItemを検索
    const item = testTargets.find(
      (target) => target.id === itemId && target.type === "item"
    );
    
    if (item) {
      setSelectedTarget(item);
      setDetailViewMode("edit"); // 編集モードで開く（ItemSpecificationPanelからの編集と同じ動作）
      setDetailViewInitialTab("behaviorDetails");
      setDetailViewOpen(true);
    } else {
      console.warn(`Item not found: ${itemId}`);
    }
  }, [testTargets]);

  // onOpenItemDetailが提供されている場合はそれを使用、そうでなければ内部ハンドラーを使用
  const effectiveOnOpenItemDetail = onOpenItemDetail || handleOpenItemDetail;

  // ワークスペース名とプロジェクト名の状態管理
  const [resolvedWorkspaceName, setResolvedWorkspaceName] = useState<string | null>(workspaceName || null);
  const [resolvedProjectName, setResolvedProjectName] = useState<string | null>(projectName || null);

  // サービス切り替え時にItem関連の状態をリセット
  useEffect(() => {
    setItemsDialogOpen(false);
    setSelectedViewTarget(null);
  }, [selectedServiceIds]);

  // 外部からのView選択時にItemサイドバーを表示
  useEffect(() => {
    if (selectedViewForItems) {
      setSelectedViewTarget(selectedViewForItems);
      setItemsDialogOpen(true);
    } else {
      // NOTE: selectedViewForItemsがnullになったときはパネルを閉じる
      setItemsDialogOpen(false);
      setSelectedViewTarget(null);
    }
  }, [selectedViewForItems]);

  // testTargetsが更新された時にselectedViewTargetも同期更新
  useEffect(() => {
    if (selectedViewTarget && testTargets.length > 0) {
      const updatedTarget = testTargets.find(
        (target) => target.id === selectedViewTarget.id
      );
      if (updatedTarget && updatedTarget !== selectedViewTarget) {
        setSelectedViewTarget(updatedTarget);
      }
    }
  }, [testTargets, selectedViewTarget]);

  // ServiceListからService詳細を開くリクエストを処理
  useEffect(() => {
    if (requestedServiceForDetail) {
      setSelectedTarget(requestedServiceForDetail);
      setDetailViewMode("view");
      setDetailViewInitialTab("basic");
      setDetailViewOpen(true);
      onOpenServiceDetailHandled?.();
    }
  }, [requestedServiceForDetail, onOpenServiceDetailHandled]);

  // URLパラメータによるターゲットダイアログ表示
  useEffect(() => {
    if (urlTargetId && testTargets.length > 0) {
      const target = testTargets.find((t) => t.id === urlTargetId);
      if (target) {
        setSelectedTarget(target);
        setDetailViewMode(urlEditMode ? "edit" : "view");
        setDetailViewOpen(true);
      }
    }
  }, [urlTargetId, testTargets, urlEditMode]);

  // ワークスペース名を取得
  useEffect(() => {
    // propsでworkspaceNameが渡されている場合はそれを使用
    if (workspaceName) {
      setResolvedWorkspaceName(workspaceName);
      return;
    }

    // workspaceIdがない場合は何もしない
    if (!workspaceId) {
      setResolvedWorkspaceName(null);
      return;
    }

    // APIからワークスペース名を取得（単一取得: UUID または slug で解決）
    const fetchWorkspaceName = async () => {
      try {
        const response = await fetch(`/api/workspaces/${workspaceId}`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const workspace = await response.json();
        if (workspace && typeof workspace.name === 'string') {
          setResolvedWorkspaceName(workspace.name);
        } else {
          setResolvedWorkspaceName(null);
        }
      } catch (error) {
        console.error("ワークスペース名取得エラー:", error);
        setResolvedWorkspaceName(null);
      }
    };

    fetchWorkspaceName();
  }, [workspaceId, workspaceName]);

  // プロジェクト名を取得
  useEffect(() => {
    // propsでprojectNameが渡されている場合はそれを使用
    if (projectName) {
      setResolvedProjectName(projectName);
      return;
    }

    // workspaceIdまたはprojectIdがない場合は何もしない
    if (!workspaceId || !projectId) {
      setResolvedProjectName(null);
      return;
    }

    // APIからプロジェクト名を取得（単一取得: UUID または slug で解決）
    const fetchProjectName = async () => {
      try {
        const response = await fetch(`/api/workspaces/${workspaceId}/projects/${projectId}`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const project = await response.json();
        if (project && typeof project.name === 'string') {
          setResolvedProjectName(project.name);
        } else {
          setResolvedProjectName(null);
        }
      } catch (error) {
        console.error("プロジェクト名取得エラー:", error);
        setResolvedProjectName(null);
      }
    };

    fetchProjectName();
  }, [workspaceId, projectId, projectName]);


  // ノードとエッジの状態管理
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

  // ReactFlow インスタンスの参照
  const reactFlowInstance = useRef<ReactFlowInstance<Node, Edge> | null>(null);
  const [hasInitialFit, setHasInitialFit] = useState(false);
  const savedViewportRef = useRef<{ x: number; y: number; zoom: number } | null>(
    null
  );
  const shouldRestoreViewportRef = useRef(false);
  const previousNodeCountRef = useRef(0);
  
  // Serviceグループノードのドラッグ状態を管理
  const [draggingServiceGroupId, setDraggingServiceGroupId] = useState<string | null>(null);
  const previousDragPositionRef = useRef<{ x: number; y: number } | null>(null);

  // サイドバーのリスト選択時に、まだ描画されていない対象ノードへ後追いでフォーカスするための保留 id。
  const [pendingFocusId, setPendingFocusId] = useState<string | null>(null);

  // 指定 id の画面ノードだけを選択状態（ハイライト）にする。targetId が null なら全解除。
  // 変化が無ければ同一参照を返し、不要な再レンダーを避ける。
  const applyHighlight = useCallback(
    (targetId: string | null) => {
      setNodes((currentNodes) => {
        let changed = false;
        const next = currentNodes.map((n) => {
          if (n.selectable === false) return n;
          const shouldSelect = n.id === targetId;
          if (n.selected === shouldSelect) return n;
          changed = true;
          return { ...n, selected: shouldSelect };
        });
        return changed ? next : currentNodes;
      });
    },
    [setNodes]
  );

  // 選択ソースに応じてハイライトとパンを制御する（issue #342 要望2 + フィードバック）。
  // 発生源は親が selectedViewSource として明示的に渡すため、sticky-ref のような
  // 状態残留やタイマー依存のレースは無く、決定的に判定できる。
  // - ハイライトは発生源に関わらず常に現在の選択へ追従させる（パネルナビでの残留を防ぐ）
  // - ビューのパンはサイドバーのリスト選択（source === "sidebar"）のときだけ予約する
  useEffect(() => {
    if (!selectedViewForItems) {
      // 選択が解除されたらハイライトと保留フォーカスを消す
      setPendingFocusId(null);
      applyHighlight(null);
      return;
    }

    const targetId = selectedViewForItems.id;
    applyHighlight(targetId);
    setPendingFocusId(selectedViewSource === "sidebar" ? targetId : null);
  }, [selectedViewForItems, selectedViewSource, applyHighlight]);

  // 保留中のパンを、ReactFlow インスタンスと対象ノードが準備でき次第ちょうど一度だけ実行する。
  // nodes を依存に含めることで、レイアウト/データ更新で対象ノードが現れたタイミングで後追い実行できる。
  useEffect(() => {
    if (!pendingFocusId) return;
    const instance = reactFlowInstance.current;
    if (!instance) return; // インスタンス未準備。nodes 更新時に再試行される。
    const targetNode = instance.getNode(pendingFocusId);
    if (!targetNode) return; // 対象ノード未描画。nodes 更新時に再試行される。

    // 対象ノードが遅れて現れた場合に備え、ハイライトも改めて適用
    applyHighlight(pendingFocusId);

    // 現在のズームを維持したまま、対象画面が中央に来るようパンのみ行う（拡大しない）
    const { zoom } = instance.getViewport();
    instance.fitView({
      nodes: [{ id: pendingFocusId }],
      duration: 600,
      minZoom: zoom,
      maxZoom: zoom,
    });

    // 一度実行したら消費（以降の nodes 更新で再パンしない）
    setPendingFocusId(null);
  }, [pendingFocusId, nodes, applyHighlight]);
  
  // エッジの表示/非表示を制御するstate
  const [showEdges, setShowEdges] = useState<boolean>(true);
  
  // エッジの制御点変更を処理するハンドラー
  const handleEdgeControlPointChange = useCallback(
    (edgeId: string, controlPoint: { x: number; y: number }) => {
      setEdges((eds) =>
        eds.map((edge) => {
          if (edge.id === edgeId) {
            return {
              ...edge,
              data: {
                ...edge.data,
                controlPoint,
                controlPointIsRelative: true, // 相対座標として保存
              },
            };
          }
          return edge;
        })
      );
    },
    [setEdges]
  );

  // 座標変換関数を取得（ReactFlowのビューポート情報を使用）
  const getScreenToFlowCoordinate = useCallback(
    (point: { x: number; y: number }) => {
      if (reactFlowInstance.current) {
        const viewport = reactFlowInstance.current.getViewport();
        return {
          x: (point.x - viewport.x) / viewport.zoom,
          y: (point.y - viewport.y) / viewport.zoom,
        };
      }
      return point;
    },
    []
  );

  // エッジタイプを動的に生成（onControlPointChangeとscreenToFlowCoordinateを注入）
  const edgeTypes = useMemo(
    () => ({
      customDraggableBezier: (props: EdgeProps) => (
        <CustomDraggableBezierEdge
          {...props}
          onControlPointChange={handleEdgeControlPointChange}
          screenToFlowCoordinate={getScreenToFlowCoordinate}
          onOpenItemDetail={effectiveOnOpenItemDetail}
        />
      ),
    }),
    [handleEdgeControlPointChange, getScreenToFlowCoordinate, effectiveOnOpenItemDetail]
  );

  // ノードとエッジの生成
  const initialElements = useMemo(() => {
    if (selectedServiceIds.size === 0) return { nodes: [], edges: [] };

    const initialNodes: Node[] = [];
    const initialEdges: Edge[] = [];
    const edgeLabelGroups = new Map<string, string[]>();
    const targetMap = new Map(testTargets.map((target) => [target.id, target]));
    
    // 選択されたServiceを取得
    const selectedServices = services.filter(s => selectedServiceIds.has(s.id));

    // 共通のデータ処理関数（機能名を含む）
    const createNodeData = (target: TestTarget) => ({
      target: {
        ...target,
        // Viewノードの場合もシンプルにname表示（featureは廃止）
        displayName: target.name,
      },
      allTargets: testTargets,
      onViewItems: (target: TestTarget) => {
        // 別ノードを直接開いたら参照ナビ履歴はリセット
        setReferenceNavStack([]);
        setSelectedViewTarget(target);
        setItemsDialogOpen(true);
        // グラフ上のノードクリック由来なのでビューは動かさない（拡大・移動しない）
        // NOTE: 親コンポーネントに通知してselectedViewForItemsを更新
        onViewSelect?.(target, "graph");
      },
    });

    // Service配下の全階層のViewを取得する関数
    const getAllViewsUnderService = (serviceId: string): TestTarget[] => {
      const getAllDescendants = (parentId: string): TestTarget[] => {
        const directChildren = testTargets.filter(
          (t) => t.parentId === parentId
        );
        let allDescendants: TestTarget[] = [];

        directChildren.forEach((child) => {
          allDescendants.push(child);
          // 再帰的に子要素を取得
          allDescendants.push(...getAllDescendants(child.id));
        });

        return allDescendants;
      };

      const allDescendants = getAllDescendants(serviceId);
      return allDescendants.filter(
        (t) => t.type === "view" || t.type === "component"
      );
    };

    // アイテムから親の画面コンテキスト（View または Component）を見つける関数（再帰的に遡る）
    const findParentView = (targetId: string): string | null => {
      const target = testTargets.find((t) => t.id === targetId);
      if (!target) return null;

      if (target.type === "view" || target.type === "component") {
        return target.id;
      }

      if (target.parentId) {
        return findParentView(target.parentId);
      }

      return null;
    };

    // ViewがどのServiceに属するかを判定する関数
    const findServiceForTarget = (targetId: string): string | null => {
      const target = testTargets.find((t) => t.id === targetId);
      if (!target) return null;
      
      if (target.type === "service") {
        return target.id;
      }
      
      if (target.parentId) {
        return findServiceForTarget(target.parentId);
      }
      
      return null;
    };

    // Serviceごとの色を割り当て
    const serviceColors = [
      "#667eea", // 紫
      "#4caf50", // 緑
      "#ff9800", // オレンジ
      "#2196f3", // 青
      "#9c27b0", // 紫
      "#f44336", // 赤
      "#00bcd4", // シアン
    ];

    const serviceColorMap = new Map<string, string>();
    selectedServices.forEach((service, index) => {
      serviceColorMap.set(service.id, serviceColors[index % serviceColors.length]);
    });

    const VIEW_CARD_COLOR = "#2196f3";
    const COMPONENT_CARD_COLOR = "#4caf50";

    const colorForViewOrComponent = (target: TestTarget): string =>
      target.type === "component" ? COMPONENT_CARD_COLOR : VIEW_CARD_COLOR;

    // 各Serviceについて処理
    const allViews: TestTarget[] = [];
    const viewServiceMap = new Map<string, string>(); // View ID -> Service ID
    const viewColorMap = new Map<string, string>();

    selectedServices.forEach((service) => {
      const serviceViews = getAllViewsUnderService(service.id);
      serviceViews.forEach((view) => {
        allViews.push(view);
        viewServiceMap.set(view.id, service.id);
        viewColorMap.set(view.id, colorForViewOrComponent(view));
      });
    });

    // Serviceごとに統計情報を計算する関数
    const getServiceStats = (serviceId: string) => {
      const getAllDescendants = (parentId: string): TestTarget[] => {
        const directChildren = testTargets.filter(
          (t) => t.parentId === parentId
        );
        let allDescendants: TestTarget[] = [];

        directChildren.forEach((child) => {
          allDescendants.push(child);
          allDescendants.push(...getAllDescendants(child.id));
        });

        return allDescendants;
      };

      const allDescendants = getAllDescendants(serviceId);
      const allViews = allDescendants.filter(
        (t) => t.type === "view" || t.type === "component"
      );

      // エントリーポイント数をカウント（View のみ）
      const entryPointCount = allViews.filter((view) => {
        if (view.type !== "view") return false;
        if (view.params && typeof view.params === "object" && "entry_point" in view.params) {
          return Boolean(view.params.entry_point);
        }
        return false;
      }).length;

      let totalItems = 0;
      let totalTransitions = 0;

      allViews.forEach((view: TestTarget) => {
        const getAllItemsUnderView = (parentId: string): number => {
          const directItems = testTargets.filter(
            (t) =>
              t.parentId === parentId &&
              !is_feature_target(t) &&
              (t.type === "item" ||
                t.type === "section" ||
                t.type === "overlay")
          );
          let itemCount = directItems.length;

          directItems.forEach((item) => {
            itemCount += getAllItemsUnderView(item.id);
            
            // 遷移数をカウント（selector 有無は問わない）
            if (item.type === "item") {
              totalTransitions += normalize_item_transitions(item.params).length;
            }
          });

          return itemCount;
        };
        totalItems += getAllItemsUnderView(view.id);
      });

      return {
        viewCount: allViews.length,
        itemCount: totalItems,
        transitionCount: totalTransitions,
        entryPointCount,
      };
    };

    // 各Serviceについて、ServiceグループノードとViewノードを作成
    const serviceNodeMap = new Map<string, Node>(); // Service ID -> Serviceグループノード
    const serviceViewNodes = new Map<string, Node[]>(); // Service ID -> Viewノード配列

    selectedServices.forEach((service) => {
      const serviceViews = getAllViewsUnderService(service.id);
      const serviceColor = serviceColorMap.get(service.id) || "#667eea";
      const stats = getServiceStats(service.id);

      // Serviceグループノードを作成（位置は後で調整）
      const serviceGroupNode: Node = {
        id: `service-group-${service.id}`,
        type: "serviceGroup",
        position: { x: 0, y: 0 }, // 後で調整
        data: {
          service,
          testTargets,
          onAddChild: (parentId: string, type: "view" | "item") => {
            onAddChild(parentId, type);
          },
          onOpenServiceDetail: (svc: TestTarget) => {
            setSelectedTarget(svc);
            setDetailViewMode("view");
            setDetailViewInitialTab("basic");
            setDetailViewOpen(true);
          },
          viewCount: stats.viewCount,
          itemCount: stats.itemCount,
          transitionCount: stats.transitionCount,
          entryPointCount: stats.entryPointCount,
          serviceColor,
        },
        draggable: true, // ドラッグを有効化（Viewノードと一緒に移動）
        // ヘッダーの専用ハンドルからのみドラッグ可能にする。
        // それ以外のドラッグはビューポートのパンになる（issue #342 要望1: 緑枠が動く問題の対策）。
        dragHandle: ".service-group-drag-handle",
        selectable: false, // 選択を無効化（Viewノードのクリックを可能にするため）
        // リサイズは無効（Viewノードの描画に合わせて自動リサイズ）
      };

      serviceNodeMap.set(service.id, serviceGroupNode);
      initialNodes.push(serviceGroupNode);

      // このService内のViewノードを作成
      const viewNodes: Node[] = [];
      serviceViews.forEach((view) => {
        const viewColor = colorForViewOrComponent(view);

        const viewNode: Node = {
          id: view.id,
          type: "custom",
          position: { x: 0, y: 0 }, // 後でレイアウト適用
          data: {
            ...createNodeData(view),
            featureColor: viewColor,
            serviceId: service.id,
          },
        };

        viewNodes.push(viewNode);
        initialNodes.push(viewNode);
      });

      serviceViewNodes.set(service.id, viewNodes);
    });

    // 利用頻度に基づいてエッジのスタイルを計算する関数
    const getUsageBasedStyle = (sourceViewId: string) => {
      const belongsToView = (candidate: TestTarget): boolean => {
        let parentId = candidate.parentId;

        while (parentId) {
          if (parentId === sourceViewId) {
            return true;
          }

          parentId = targetMap.get(parentId)?.parentId;
        }

        return false;
      };

      if (!testcaseMappings?.testTargetToTestcases) {
        return {
          strokeWidth: 1.5,
          stroke: "#2196f3",
          strokeDasharray: "none",
        };
      }

      // sourceViewId配下の全アイテムの利用頻度を集計
      const itemsInView = testTargets.filter((candidate) =>
        (candidate.type === "item" ||
          candidate.type === "section" ||
          candidate.type === "overlay") &&
        belongsToView(candidate)
      );

      // アイテムの利用頻度を合計
      let totalUsage = 0;
      itemsInView.forEach((item) => {
        const mappings = testcaseMappings.testTargetToTestcases.get(item.id);
        if (mappings) {
          totalUsage += mappings.length;
        }
      });

      // 利用頻度に応じてスタイルを調整（3段階）
      // 青(#2196f3)を基準に、頻度が下がるごとに彩度を落としてグレーに近づける
      if (totalUsage >= 11) {
        // よく使う（11回以上）: 濃い青、太めの実線
        return {
          strokeWidth: 2.5,
          stroke: "#1976d2", // 濃い青
          strokeDasharray: "none",
        };
      } else if (totalUsage >= 3) {
        // たまに使う（3-10回）: 標準の青、長めの破線
        return {
          strokeWidth: 1.5,
          stroke: "#2196f3", // 標準の青
          strokeDasharray: "8,4",
        };
      } else {
        // ほぼ使わない（0-2回）: 薄い青、短い破線
        return {
          strokeWidth: 1,
          stroke: "#90caf9", // 薄い青
          strokeDasharray: "5,5",
        };
      }
    };

    const getTransitionStyle = (
      transition: { sourceViewId: string; targetViewId: string },
      sourceViewId: string
    ) => {
      return getUsageBasedStyle(sourceViewId);
    };


    // Itemの遷移情報から画面遷移エッジを生成
    // より柔軟な遷移データ検出
    const itemsWithTransitions = testTargets.filter((target) => {
      if (
        target.type !== "item" &&
        target.type !== "section" &&
        target.type !== "overlay"
      )
        return false;
      // NOTE: selector 無し Item（CSV インポート等）の transitions もグラフに載せる
      return has_item_transitions(target.params);
    });

    // デバッグ: View-Item関係の確認
    itemsWithTransitions.forEach((item, index) => {
      const transitions = normalize_item_transitions(item.params);

      const sourceViewId = findParentView(item.id); // アイテムから親Viewを再帰的に検索

      // sourceViewIdがnullの場合はスキップ
      if (!sourceViewId) {
        console.warn(
          `❌ ${item.name}: 親Viewが見つかりません (親階層を遡っても見つからない)`
        );
        return;
      }

      // 各transitionに対してエッジを作成
      transitions.forEach((transition, transitionIndex) => {
        const targetViewId = String(transition.targetId); // 明示的にStringに変換

        // ノードの存在チェック
        const sourceExists = initialNodes.some((n) => n.id === sourceViewId);
        const targetExists = initialNodes.some((n) => n.id === targetViewId);

        if (sourceExists && targetExists && sourceViewId && targetViewId) {
          const sourceServiceId = findServiceForTarget(sourceViewId);
          const targetServiceId = findServiceForTarget(targetViewId);
          const isCrossService = sourceServiceId !== targetServiceId;

          const transitionData = {
            id: `item-transition-${index}-${transitionIndex}`,
            sourceViewId,
            targetViewId,
            label: `${item.name}`,
            description: transition.description,
          };

          let style = getTransitionStyle(transitionData, sourceViewId);
          
          // Service間のエッジの場合は異なるスタイルを適用
          if (isCrossService) {
            style = {
              ...style,
              stroke: "#9c27b0", // 紫
              strokeWidth: 2,
              strokeDasharray: "10,5", // 長めの破線
            };
          }
          
          // エッジをServiceグループノードより上に表示するため、z-indexを追加
          style = {
            ...style,
            pointerEvents: "all", // エッジのクリックを可能にする
          } as any;

          const edge = {
            id: transitionData.id,
            source: sourceViewId,
            target: targetViewId,
            type: "customDraggableBezier",
            style,
            zIndex: 100, // Serviceグループノード（z-index: 1）より上に配置
            label: item.name,
            labelStyle: {
              fill: style.stroke,
              fontWeight: 500,
              fontSize: "12px",
            },
            labelBgStyle: {
              fill: "white",
              fillOpacity: 0.8,
              strokeWidth: 1,
              stroke: style.stroke,
            },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: style.stroke,
            },
            // エッジをクリック・選択可能にする
            selectable: true,
            focusable: true,
            // 選択時のスタイル（太く、濃い青色で強調）
            data: {
              originalStyle: style,
              itemName: item.name,
              description: transition.description,
              isCrossService, // Service間エッジかどうかを記録
              itemId: item.id, // ItemのIDを追加（ダブルクリックで遷移情報が表示されている動作詳細タブを開くため）
            },
          };

          initialEdges.push(edge);
          const labelGroupKey = `${sourceViewId}->${targetViewId}`;
          if (!edgeLabelGroups.has(labelGroupKey)) {
            edgeLabelGroups.set(labelGroupKey, []);
          }
          edgeLabelGroups.get(labelGroupKey)!.push(edge.id);
        } else {
          console.warn(
            `❌ エッジ作成失敗: ${item.name} (ソース:${sourceExists}, ターゲット:${targetExists})`
          );
        }
      });
    });

    // Serviceごとにレイアウトを適用
    const serviceLayouts = new Map<string, { nodes: Node[]; edges: Edge[] }>();
    
    selectedServices.forEach((service) => {
      const viewNodes = serviceViewNodes.get(service.id) || [];
      const serviceEdges = initialEdges.filter((edge) => {
        const sourceServiceId = findServiceForTarget(edge.source);
        const targetServiceId = findServiceForTarget(edge.target);
        return sourceServiceId === service.id && targetServiceId === service.id;
      });

      if (viewNodes.length > 0) {
        const layouted = getLayoutedElements(viewNodes, serviceEdges, "TB");
        serviceLayouts.set(service.id, layouted);
      }
    });

    // Service間の配置を調整（縦並び）
    const serviceSpacing = 100; // Service間の間隔
    const padding = 24; // Serviceグループノードのパディング
    const headerHeight = 100; // ヘッダーの高さ
    const nodeMargin = 20; // Viewノード間のマージン
    let currentY = 50;

    selectedServices.forEach((service, serviceIndex) => {
      const serviceGroupNode = serviceNodeMap.get(service.id);
      const layouted = serviceLayouts.get(service.id);
      
      if (serviceGroupNode && layouted && layouted.nodes.length > 0) {
        // Serviceグループノードの位置を先に設定
        const groupX = 50;
        serviceGroupNode.position = {
          x: groupX,
          y: currentY,
        };

        // Viewノードの位置をServiceグループノード内に配置
        const minX = Math.min(...layouted.nodes.map((n) => n.position.x));
        const minY = Math.min(...layouted.nodes.map((n) => n.position.y));
        
        // Viewノードの位置を調整（Serviceグループノードの位置を基準とした絶対位置）
        layouted.nodes.forEach((viewNode) => {
          const existingNode = initialNodes.find((n) => n.id === viewNode.id);
          if (existingNode) {
            // Serviceグループノード内の相対位置を計算
            const relativeX = padding + (viewNode.position.x - minX) + nodeMargin;
            const relativeY = padding + headerHeight + (viewNode.position.y - minY) + nodeMargin;
            
            // Serviceグループノードの位置を基準とした絶対位置に変換
            existingNode.position = {
              x: groupX + relativeX,
              y: currentY + relativeY,
            };
          }
        });

        // Viewノードの範囲を計算（Serviceグループノード内の相対位置）
        const relativePositions = layouted.nodes.map((n) => {
          const existingNode = initialNodes.find((node) => node.id === n.id);
          if (!existingNode) return { x: 0, y: 0 };
          
          // Serviceグループノードの位置を基準とした相対位置
          return {
            x: existingNode.position.x - groupX,
            y: existingNode.position.y - currentY,
          };
        });

        const maxX = Math.max(...relativePositions.map((p) => p.x + 280)); // ノード幅を考慮
        const maxY = Math.max(...relativePositions.map((p) => p.y + 150)); // ノード高さを考慮

        // Serviceグループノードのサイズを計算
        const groupWidth = Math.max(maxX + padding, 1250); /* 統計項目とボタンが折り返されない最小幅を確保 */
        const groupHeight = Math.max(maxY + padding, headerHeight + 200);
        
        // Serviceグループノードのサイズをdataに保存
        (serviceGroupNode.data as any).width = groupWidth;
        (serviceGroupNode.data as any).height = groupHeight;

        // 次のServiceのY位置を計算
        currentY += groupHeight + serviceSpacing;
      } else if (serviceGroupNode) {
        // Viewノードがない場合
        serviceGroupNode.position = {
          x: 50,
          y: currentY,
        };
        (serviceGroupNode.data as any).width = 1250; /* 統計項目とボタンが折り返されない最小幅を確保 */
        (serviceGroupNode.data as any).height = 200; /* Service名（2行）+ ヘッダー高さ + パディングを考慮した最小高さを確保 */
        currentY += 200 + serviceSpacing;
      }
    });

    const edgeLabelMeta = new Map<string, number>();
    edgeLabelGroups.forEach((edgeIds) => {
      if (edgeIds.length === 0) return;
      const center = (edgeIds.length - 1) / 2;
      edgeIds.forEach((edgeId, idx) => {
        edgeLabelMeta.set(edgeId, edgeIds.length === 1 ? 0 : idx - center);
      });
    });

    const edgesWithLabelOffsets = initialEdges.map((edge) => {
      const offsetIndex = edgeLabelMeta.get(edge.id);
      if (offsetIndex === undefined || offsetIndex === 0) {
        return edge;
      }
      return {
        ...edge,
        data: {
          ...(edge.data || {}),
          labelOffsetIndex: offsetIndex,
        },
      };
    });

    return { nodes: initialNodes, edges: edgesWithLabelOffsets };
  }, [services, selectedServiceIds, testTargets, testcaseMappings, onAddChild]);

  // 要素の初期化
  React.useEffect(() => {
    const instance = reactFlowInstance.current;
    const previousNodeCount = previousNodeCountRef.current;
    const nextNodeCount = initialElements.nodes.length;
    const hadNodesBefore = previousNodeCount > 0;
    const willHaveNodes = nextNodeCount > 0;

    if (instance && hadNodesBefore) {
      savedViewportRef.current = instance.getViewport();
    }

    shouldRestoreViewportRef.current =
      Boolean(savedViewportRef.current) && hadNodesBefore && willHaveNodes;

    // 既存のノード位置を保持してマージ
    setNodes((currentNodes) => {
      // 既存のノード位置をマップに保存
      const positionMap = new Map<string, { x: number; y: number }>();
      currentNodes.forEach((node) => {
        if (node.position && (node.type === "serviceGroup" || node.type === "custom")) {
          positionMap.set(node.id, node.position);
        }
      });

      // 新しいノードに既存の位置を適用
      const nodesWithPreservedPositions = initialElements.nodes.map((newNode) => {
        const existingPosition = positionMap.get(newNode.id);
        if (existingPosition) {
          return {
            ...newNode,
            position: existingPosition,
          };
        }
        return newNode;
      });

      return nodesWithPreservedPositions;
    });
    setEdges(initialElements.edges);

    if (!hadNodesBefore && willHaveNodes) {
      // 初回描画や空状態からの復帰時のみfitViewを許可
      setHasInitialFit(false);
    } else if (!willHaveNodes) {
      setHasInitialFit(true);
    }

    previousNodeCountRef.current = nextNodeCount;
  }, [initialElements, setNodes, setEdges]);

  useEffect(() => {
    if (
      shouldRestoreViewportRef.current &&
      savedViewportRef.current &&
      reactFlowInstance.current
    ) {
      // NOTE: Restore the previously saved viewport when service visibility changes
      reactFlowInstance.current.setViewport(savedViewportRef.current);
      shouldRestoreViewportRef.current = false;
      savedViewportRef.current = null;
    }
  }, [nodes]);

  // データ取得完了後にFit Viewを実行
  useEffect(() => {
    if (
      !loading &&
      reactFlowInstance.current &&
      nodes.length > 0 &&
      !hasInitialFit
    ) {
      // データ取得完了後、少し遅延してfitViewを実行
      const timer = setTimeout(() => {
        reactFlowInstance.current?.fitView({
          ...DEFAULT_FIT_VIEW_OPTIONS,
        });
        setHasInitialFit(true);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [loading, nodes, hasInitialFit]);

  const onConnect = useCallback((params: Connection) => {
    // 必要に応じて接続処理を実装
  }, []);

  // Serviceグループノードのサイズを更新する関数
  const updateServiceGroupSizes = useCallback(() => {
    // ドラッグ中はサイズ更新をスキップ
    if (draggingServiceGroupId) return;
    
    setNodes((currentNodes) => {
      const updatedNodes = [...currentNodes];
      const padding = 24;
      const headerHeight = 100;
      const nodeMargin = 20;

      // 各Serviceグループノードについて処理
      selectedServiceIds.forEach((serviceId) => {
        const serviceGroupNode = updatedNodes.find(
          (n) => n.id === `service-group-${serviceId}`
        );
        if (!serviceGroupNode) return;

        // このServiceに属するViewノードを取得
        const viewNodes = updatedNodes.filter(
          (n) =>
            n.type === "custom" &&
            (n.data as any)?.serviceId === serviceId &&
            n.id !== serviceGroupNode.id
        );

        if (viewNodes.length === 0) {
          // Viewノードがない場合
          (serviceGroupNode.data as any).width = 1250; /* 統計項目とボタンが折り返されない最小幅を確保 */
          (serviceGroupNode.data as any).height = 200; /* Service名（2行）+ ヘッダー高さ + パディングを考慮した最小高さを確保 */
          return;
        }

        // Viewノードの範囲を計算（Serviceグループノードの位置を基準とした相対位置）
        const groupX = serviceGroupNode.position.x;
        const groupY = serviceGroupNode.position.y;

        const relativePositions = viewNodes.map((n) => ({
          x: n.position.x - groupX,
          y: n.position.y - groupY,
        }));

        const minX = Math.min(...relativePositions.map((p) => p.x));
        const minY = Math.min(...relativePositions.map((p) => p.y));
        const maxX = Math.max(...relativePositions.map((p) => p.x + 280)); // ノード幅を考慮
        const maxY = Math.max(...relativePositions.map((p) => p.y + 150)); // ノード高さを考慮

        // Serviceグループノードのサイズを計算
              const groupWidth = Math.max(maxX + padding * 2, 1250); /* 統計項目とボタンが折り返されない最小幅を確保 */
              const groupHeight = Math.max(maxY + padding * 2, headerHeight + 200);

        (serviceGroupNode.data as any).width = groupWidth;
        (serviceGroupNode.data as any).height = groupHeight;
      });

      return updatedNodes;
    });
  }, [selectedServiceIds, setNodes, draggingServiceGroupId]);

  // カスタムノード変更ハンドラー（fitViewを組み込み）
  const handleNodesChange = useCallback(
    (changes: any) => {
      onNodesChange(changes);

      // ノードが追加された時にfitViewを実行
      const hasAddition = changes.some((change: any) => change.type === "add");
      if (hasAddition && reactFlowInstance.current) {
        setTimeout(() => {
          reactFlowInstance.current?.fitView({
            ...DEFAULT_FIT_VIEW_OPTIONS,
          });
        }, 200);
      }

      // ノードの位置が変更されたときにServiceグループノードのサイズを更新
      // ただし、Serviceグループノードのドラッグ中はスキップ（onNodeDragで処理）
      const hasPositionChange = changes.some(
        (change: any) => change.type === "position" && change.position
      );
      
      // Serviceグループノードのドラッグ中は、Viewノードの位置変更もスキップ
      const isServiceGroupDragging = changes.some(
        (change: any) => 
          change.type === "position" && 
          change.position && 
          change.id &&
          change.id.startsWith("service-group-")
      );
      
      if (hasPositionChange && !draggingServiceGroupId && !isServiceGroupDragging) {
        // ViewノードがServiceグループノードの範囲内に収まるように制限し、サイズも更新
        setTimeout(() => {
          setNodes((currentNodes) => {
            const updatedNodes = [...currentNodes];
            const padding = 24;
            const headerHeight = 100;

            // Viewノードの位置を制限（Serviceグループノードの範囲内に収める）
            // ただし、ドラッグ中のViewノードはスキップ
            updatedNodes.forEach((node) => {
              if (node.type !== "custom") return;
              // ドラッグ中のノードはスキップ
              if ((node as any).dragging) return;

              const serviceId = (node.data as any)?.serviceId;
              if (!serviceId) return;

              const serviceGroupNode = updatedNodes.find(
                (n) => n.id === `service-group-${serviceId}`
              );
              if (!serviceGroupNode) return;

              const groupX = serviceGroupNode.position.x;
              const groupY = serviceGroupNode.position.y;
              const groupWidth = (serviceGroupNode.data as any)?.width || 400;
              const groupHeight = (serviceGroupNode.data as any)?.height || 300;

              // Viewノードの位置をServiceグループノードの範囲内に制限
              const minX = groupX + padding;
              const minY = groupY + headerHeight + padding;
              const maxX = groupX + groupWidth - padding - 280; // ノード幅を考慮
              const maxY = groupY + groupHeight - padding - 150; // ノード高さを考慮

              node.position = {
                x: Math.max(minX, Math.min(maxX, node.position.x)),
                y: Math.max(minY, Math.min(maxY, node.position.y)),
              };
            });

            // 次に、Serviceグループノードのサイズを更新
            selectedServiceIds.forEach((serviceId) => {
              const serviceGroupNode = updatedNodes.find(
                (n) => n.id === `service-group-${serviceId}`
              );
              if (!serviceGroupNode) return;

              const viewNodes = updatedNodes.filter(
                (n) =>
                  n.type === "custom" &&
                  (n.data as any)?.serviceId === serviceId &&
                  n.id !== serviceGroupNode.id
              );

              if (viewNodes.length === 0) {
                (serviceGroupNode.data as any).width = 1250; /* 統計項目とボタンが折り返されない最小幅を確保 */
                (serviceGroupNode.data as any).height = 200; /* Service名（2行）+ ヘッダー高さ + パディングを考慮した最小高さを確保 */
                return;
              }

              const groupX = serviceGroupNode.position.x;
              const groupY = serviceGroupNode.position.y;

              const relativePositions = viewNodes.map((n) => ({
                x: n.position.x - groupX,
                y: n.position.y - groupY,
              }));

              const minX = Math.min(...relativePositions.map((p) => p.x));
              const minY = Math.min(...relativePositions.map((p) => p.y));
              const maxX = Math.max(...relativePositions.map((p) => p.x + 280));
              const maxY = Math.max(...relativePositions.map((p) => p.y + 150));

              const groupWidth = Math.max(maxX + padding * 2, 1250); /* 統計項目とボタンが折り返されない最小幅を確保 */
              const groupHeight = Math.max(maxY + padding * 2, headerHeight + 200);

              (serviceGroupNode.data as any).width = groupWidth;
              (serviceGroupNode.data as any).height = groupHeight;
            });

            return updatedNodes;
          });
        }, 50);
      }
    },
    [onNodesChange, updateServiceGroupSizes, nodes, draggingServiceGroupId]
  );

  // Serviceグループノードのドラッグ開始ハンドラー
  const handleNodeDragStart = useCallback(
    (event: React.MouseEvent, node: Node) => {
      if (node.id.startsWith("service-group-")) {
        const serviceId = node.id.replace("service-group-", "");
        setDraggingServiceGroupId(serviceId);
        previousDragPositionRef.current = { x: node.position.x, y: node.position.y };

        // このServiceに属するViewノードを選択状態にする
        setNodes((currentNodes) => {
          return currentNodes.map((n) => {
            if (
              n.type === "custom" &&
              (n.data as any)?.serviceId === serviceId
            ) {
              return { ...n, selected: true };
            }
            return n;
          });
        });
      }
    },
    [setNodes]
  );

  // Serviceグループノードのドラッグ中ハンドラー
  const handleNodeDrag = useCallback(
    (event: React.MouseEvent, node: Node) => {
      if (node.id.startsWith("service-group-") && previousDragPositionRef.current) {
        const serviceId = node.id.replace("service-group-", "");
        // 前回の位置からの差分を計算
        const deltaX = node.position.x - previousDragPositionRef.current.x;
        const deltaY = node.position.y - previousDragPositionRef.current.y;

        if (deltaX !== 0 || deltaY !== 0) {
          // 前回の位置を更新（次のドラッグイベント用）
          previousDragPositionRef.current = { x: node.position.x, y: node.position.y };
          
          // このServiceに属するViewノードも一緒に移動（相対位置を維持）
          setNodes((currentNodes) => {
            return currentNodes.map((n) => {
              if (
                n.type === "custom" &&
                (n.data as any)?.serviceId === serviceId
              ) {
                // 現在の位置に差分を加算（相対位置を維持）
                return {
                  ...n,
                  position: {
                    x: n.position.x + deltaX,
                    y: n.position.y + deltaY,
                  },
                  // ドラッグ中であることを示すフラグを設定（handleNodesChangeでスキップするため）
                  dragging: true,
                };
              }
              return n;
            });
          });
        }
      }
    },
    [setNodes]
  );

  // Serviceグループノードのドラッグ終了ハンドラー
  const handleNodeDragStop = useCallback(
    (event: React.MouseEvent, node: Node) => {
      if (node.id.startsWith("service-group-")) {
        const serviceId = node.id.replace("service-group-", "");
        setDraggingServiceGroupId(null);
        previousDragPositionRef.current = null;

        // Viewノードの選択を解除し、ドラッグフラグも解除
        // また、Viewノードの位置をServiceグループノードの範囲内に制限
        setNodes((currentNodes) => {
          const updatedNodes = currentNodes.map((n) => {
            if (
              n.type === "custom" &&
              (n.data as any)?.serviceId === serviceId
            ) {
              const { dragging, ...rest } = n as any;
              return { ...rest, selected: false };
            }
            return n;
          });

          // Serviceグループノードを取得
          const serviceGroupNode = updatedNodes.find(
            (n) => n.id === `service-group-${serviceId}`
          );
          if (serviceGroupNode) {
            const padding = 24;
            const headerHeight = 100;
            const groupX = serviceGroupNode.position.x;
            const groupY = serviceGroupNode.position.y;
            const groupWidth = (serviceGroupNode.data as any)?.width || 400;
            const groupHeight = (serviceGroupNode.data as any)?.height || 300;

            // Viewノードの位置をServiceグループノードの範囲内に制限
            updatedNodes.forEach((node) => {
              if (node.type !== "custom") return;
              const nodeServiceId = (node.data as any)?.serviceId;
              if (nodeServiceId !== serviceId) return;

              const minX = groupX + padding;
              const minY = groupY + headerHeight + padding;
              const maxX = groupX + groupWidth - padding - 280;
              const maxY = groupY + groupHeight - padding - 150;

              node.position = {
                x: Math.max(minX, Math.min(maxX, node.position.x)),
                y: Math.max(minY, Math.min(maxY, node.position.y)),
              };
            });
          }

          return updatedNodes;
        });

        // Serviceグループノードのサイズを更新
        setTimeout(() => {
          updateServiceGroupSizes();
        }, 100);
      }
    },
    [setNodes, updateServiceGroupSizes]
  );

  const handleAddChild = (parentId: string, type: "view" | "item") => {
    onAddChild(parentId, type);
  };

  // カスタムコントロールのハンドラー
  const handleZoomIn = useCallback(() => {
    if (reactFlowInstance.current) {
      reactFlowInstance.current.zoomIn();
    }
  }, []);

  const handleZoomOut = useCallback(() => {
    if (reactFlowInstance.current) {
      reactFlowInstance.current.zoomOut();
    }
  }, []);

  const handleFitView = useCallback(() => {
    if (reactFlowInstance.current) {
      reactFlowInstance.current.fitView({
        ...DEFAULT_FIT_VIEW_OPTIONS,
      });
    }
  }, []);

  // ズームレベルの状態を管理（ボタンの無効化に使用）
  const [viewport, setViewport] = useState({ zoom: 1 });

  // エッジの表示状態に応じてhiddenプロパティを設定
  const displayEdges = useMemo(() => {
    return edges.map((edge) => ({
      ...edge,
      hidden: !showEdges ? true : undefined,
    })) as Edge[];
  }, [edges, showEdges]);
  const handleViewportChange = useCallback(() => {
    if (reactFlowInstance.current) {
      const currentViewport = reactFlowInstance.current.getViewport();
      setViewport(currentViewport);
    }
  }, []);

  // 後方互換性のためのレガシー関数（必要に応じて削除可能）
  const getItemsForView = (viewTarget: TestTarget) => {
    const buildItemHierarchy = (
      parentId: string,
      level: number = 0
    ): (TestTarget & { level: number })[] => {
      const directChildren = testTargets.filter(
        (t) =>
          t.parentId === parentId &&
          !is_feature_target(t) &&
          (t.type === "item" || t.type === "section" || t.type === "overlay")
      );

      let result: (TestTarget & { level: number })[] = [];

      directChildren.forEach((child) => {
        // 現在のアイテムを追加（レベル情報付き）
        result.push({ ...child, level });

        // 子アイテムを再帰的に追加
        const grandChildren = buildItemHierarchy(child.id, level + 1);
        result.push(...grandChildren);
      });

      return result;
    };

    return buildItemHierarchy(viewTarget.id);
  };

  useLayoutEffect(() => {
    return () => {
      pageHeaderResizeObserverRef.current?.disconnect();
      pageHeaderResizeObserverRef.current = null;
      document.documentElement.style.removeProperty(
        TTM_PAGE_HEADER_HEIGHT_CSS_VAR
      );
    };
  }, []);

  // ワークスペース名とプロジェクト名の表示用テキスト
  // NOTE: 取得した名前を優先し、なければID、それもなければデフォルト値を表示
  const displayWorkspaceName = resolvedWorkspaceName || workspaceName || workspaceId || "ワークスペース";
  const displayProjectName = resolvedProjectName || projectName || projectId || "プロジェクト";

  if (selectedServiceIds.size === 0) {
    return (
      <Container>
        <EmptyState>
          <ViewModuleIcon
            style={{ fontSize: "4rem", marginBottom: "16px", opacity: 0.5 }}
          />
          <Typography variant="h6" style={{ marginBottom: "8px" }}>
            サービスを選択してください
          </Typography>
          <Typography variant="body2" color="textSecondary">
            左側のサービス一覧から管理したいサービスを選択してください
          </Typography>
        </EmptyState>
      </Container>
    );
  }

  const selectedServices = services.filter(s => selectedServiceIds.has(s.id));

  return (
    <Container>
      {/* メインコンテンツ - ネットワークグラフ */}
      <MainContent>
        {/* ヘッダー */}
        <PageHeader ref={attachPageHeaderMeasurements}>
          <PageHeaderLeft>
            {displayWorkspaceName} &gt; {displayProjectName}
          </PageHeaderLeft>
          <PageHeaderRight />
        </PageHeader>

        {selectedServices.length > 0 ? (
          <>

            <FlowContainer>
              <ReactFlow
                nodes={nodes}
                edges={displayEdges}
                onNodesChange={handleNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onNodeDragStart={handleNodeDragStart}
                onNodeDrag={handleNodeDrag}
                onNodeDragStop={handleNodeDragStop}
                onInit={(instance) => {
                  reactFlowInstance.current = instance;
                  // 初期ビューポートの状態を設定
                  const initialViewport = instance.getViewport();
                  setViewport(initialViewport);
                  // 初期化直後、データ取得が完了している場合のみfitViewを実行
                  setTimeout(() => {
                    if (!loading && nodes.length > 0 && !hasInitialFit) {
                      setHasInitialFit(true);
                    }
                  }, 200);
                }}
                onViewportChange={() => {
                  // ビューポート変更時、データ取得完了後の初回のみfitViewを実行
                  if (
                    !loading &&
                    reactFlowInstance.current &&
                    nodes.length > 0 &&
                    !hasInitialFit
                  ) {
                    setTimeout(() => {
                      reactFlowInstance.current?.fitView({
                        ...DEFAULT_FIT_VIEW_OPTIONS,
                      });
                      setHasInitialFit(true);
                    }, 100);
                  }
                  // ズームレベルの状態を更新
                  handleViewportChange();
                }}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                nodesDraggable={true}
                nodesConnectable={false}
                elementsSelectable={true}
                minZoom={VIEWPORT_MIN_ZOOM}
                maxZoom={VIEWPORT_MAX_ZOOM}
                fitView
                fitViewOptions={DEFAULT_FIT_VIEW_OPTIONS}
              >
                {/* グラフエリア内の左下に表示されるカスタムコントロールパネル */}
                <CustomControlsPanel>
                  <Tooltip title="拡大" placement="right">
                    <ControlButton
                      onClick={handleZoomIn}
                      disabled={viewport.zoom >= VIEWPORT_MAX_ZOOM}
                      aria-label="拡大"
                    >
                      <ZoomInIcon />
                    </ControlButton>
                  </Tooltip>
                  <Tooltip title="縮小" placement="right">
                    <ControlButton
                      onClick={handleZoomOut}
                      disabled={viewport.zoom <= VIEWPORT_MIN_ZOOM}
                      aria-label="縮小"
                    >
                      <ZoomOutIcon />
                    </ControlButton>
                  </Tooltip>
                  <Tooltip title="ビュー全体表示" placement="right">
                    <ControlButton
                      onClick={handleFitView}
                      aria-label="ビュー全体表示"
                    >
                      <FitScreenIcon />
                    </ControlButton>
                  </Tooltip>
                  <Tooltip
                    title={showEdges ? "エッジを非表示" : "エッジを表示"}
                    placement="right"
                  >
                    <ControlButton
                      onClick={() => setShowEdges(!showEdges)}
                      aria-label={showEdges ? "エッジを非表示" : "エッジを表示"}
                    >
                      {showEdges ? <VisibilityIcon /> : <VisibilityOffIcon />}
                    </ControlButton>
                  </Tooltip>
                </CustomControlsPanel>
                <MiniMap
                  pannable
                  zoomable
                  nodeColor={(node) => {
                    const target = (node.data as any)?.target;
                    switch (target?.type) {
                      case "service":
                        return "#667eea";
                      case "view":
                        return "#4caf50";
                      case "item":
                        return "#9c27b0";
                      default:
                        return "#ccc";
                    }
                  }}
                />
                <Background
                  variant={BackgroundVariant.Dots}
                  gap={20}
                  size={1}
                />
              </ReactFlow>
            </FlowContainer>
          </>
        ) : (
          <EmptyState>
            <ViewModuleIcon
              style={{ fontSize: "4rem", marginBottom: "16px", opacity: 0.5 }}
            />
            <Typography variant="h6" style={{ marginBottom: "8px" }}>
              サービスを選択してください
            </Typography>
            <Typography variant="body2" color="textSecondary">
              左側のサービス一覧から管理したいサービスを選択してください
            </Typography>
          </EmptyState>
        )}
      </MainContent>

      {/* 右サイドバー - ピークビュー（オーバーレイ表示） */}
      <RightSidebar
        isOpen={itemsDialogOpen}
        width={peekViewWidth}
        headerOffsetPx={pageHeaderHeightPx}
      >
        <ItemSpecificationPanel
          isOpen={itemsDialogOpen}
          onClose={() => {
            setItemsDialogOpen(false);
            setReferenceNavStack([]);
            // NOTE: パネルを閉じたときに親に通知してselectedViewForItemsをリセット
            onViewPanelClose?.();
          }}
          viewTarget={selectedViewTarget}
          items={selectedViewTarget ? getItemsForView(selectedViewTarget) : []}
          testcaseMappings={testcaseMappings}
          onEditItem={(item: TestTarget) => {
            setSelectedTarget(item);
            setDetailViewMode("edit");
            setDetailViewOpen(true);
          }}
          onViewItem={(item: TestTarget) => {
            setSelectedTarget(item);
            setDetailViewMode("view");
            setDetailViewOpen(true);
          }}
          onDeleteItem={async (itemId, options) => {
            await onDeleteTarget?.(itemId, options);
          }}
          onDeleteView={onDeleteTarget}
          onAddItem={() => {
            if (selectedViewTarget) {
              handleAddChild(selectedViewTarget.id, "item");
            }
            // アイテム追加後はパネルを開いたままにして、新しいアイテムが表示されるのを確認できるようにする
          }}
          onWidthChange={setPeekViewWidth}
          apiClient={apiClient || undefined}
          workspaceId={workspaceId}
          projectId={projectId}
          allTestTargets={testTargets}
          onOpenReferenceTarget={(target: TestTarget) => {
            // 現在のパネル対象を退避してから参照先へ切り替え（戻る導線で復帰可能に）
            if (selectedViewTarget) {
              setReferenceNavStack((prev) => [...prev, selectedViewTarget]);
            }
            setSelectedViewTarget(target);
            setItemsDialogOpen(true);
            // パネル内ナビ由来なのでビューは動かさない
            onViewSelect?.(target, "panel");
          }}
          canGoBackToPreviousTarget={referenceNavStack.length > 0}
          onBackToPreviousTarget={() => {
            setReferenceNavStack((prev) => {
              if (prev.length === 0) return prev;
              const next = prev.slice(0, -1);
              const back = prev[prev.length - 1];
              setSelectedViewTarget(back);
              setItemsDialogOpen(true);
              // パネル内ナビ由来なのでビューは動かさない
              onViewSelect?.(back, "panel");
              return next;
            });
          }}
          onRefresh={onRefresh}
          onUpdateTestTarget={onUpdateTestTarget}
          operationLogService={operationLogService}
          // userToken removed - using Auth0 authentication
          apiConfig={apiConfig}
        />
      </RightSidebar>

      {/* 詳細表示ダイアログ */}
      <TestTargetDetailView
        open={detailViewOpen}
        backdropInsetTopPx={pageHeaderHeightPx}
        onClose={() => {
          setDetailViewOpen(false);
          setDetailViewInitialTab("basic"); // 初期タブをリセット
          // URLからダイアログを開いた場合はURLも更新
          if (urlTargetId && onCloseDialog) {
            onCloseDialog();
          }
        }}
        testTarget={selectedTarget}
        testTargets={testTargets}
        testcaseMappings={testcaseMappings}
        apiClient={apiClient}
        workspaceId={workspaceId}
        projectId={projectId}
        initialMode={detailViewMode}
        initialTab={detailViewInitialTab}
        onSave={async (updatedTarget) => {
          try {
            if (!apiClient || !workspaceId || !projectId) {
              throw new Error(
                "API設定またはワークスペース・プロジェクト情報が不足しています"
              );
            }

            let createdTestTarget: TestTarget | null = null;

            if (selectedTarget && selectedTarget.id) {
              // 既存のTestTargetの更新
              await apiClient.updateTestTarget(
                workspaceId,
                projectId,
                selectedTarget.id,
                updatedTarget
              );
            } else {
              // バリデーション
              if (!updatedTarget.name || !updatedTarget.type) {
                throw new Error("名前と種別は必須です");
              }

              // 動的にtypeIdを取得
              const typeId = await getTypeIdFromTypeName(
                updatedTarget.type,
                apiClient,
                workspaceId,
                projectId
              );
              if (!typeId) {
                throw new Error(`未知の種別です: ${updatedTarget.type}`);
              }

              // TestTargetFormDataをTestTargetCreateInputに変換
              const createInput = {
                name: updatedTarget.name.trim(),
                displayOrder: updatedTarget.displayOrder || 0,
                typeId: typeId, // 正しいtypeIdを使用
                description: updatedTarget.description || undefined,
                parentTestTargetId: updatedTarget.parentId || undefined,
                params: JSON.stringify(updatedTarget.params || {}), // JSON文字列に変換
              };

              const createdTargetResult = await apiClient.createTestTargets(
                workspaceId,
                projectId,
                [createInput]
              );
              const createdTarget = createdTargetResult[0];

              if (!createdTarget) {
                throw new Error("TestTargetの作成結果が取得できませんでした");
              }

              createdTestTarget = toCreatedTestTarget(
                createdTarget,
                updatedTarget
              );

              if (onAddTestTarget) {
                await onAddTestTarget(createdTestTarget);
              }

              // 新規作成時はダイアログを閉じずに参照モードへ切り替え、リロードを避ける
              setSelectedTarget(createdTestTarget);
              setDetailViewMode("view");
              setDetailViewOpen(true);
            }

            if (createdTestTarget) {
              return;
            }

            // ローカル状態更新またはリフレッシュ
            if (onUpdateTestTarget && selectedTarget) {
              const updatedTestTarget: TestTarget = {
                ...selectedTarget,
                ...updatedTarget,
                id: selectedTarget.id,
                metaInfo: selectedTarget.metaInfo,
              };
              onUpdateTestTarget(updatedTestTarget);

              // TestTargetDetailViewの状態も更新
              setSelectedTarget(updatedTestTarget);
            } else {
              onRefresh();
            }
          } catch (error) {
            console.error("TestTarget保存エラー:", error);
            showError(
              `保存に失敗しました: ${
                error instanceof Error ? error.message : "不明なエラー"
              }`
            );
          }
        }}
        onDelete={
          selectedTarget?.type === "service" &&
          selectedTarget.id &&
          onDeleteTarget
            ? async () => {
                const sid = selectedTarget.id as string;
                try {
                  await onDeleteTarget(sid);
                  setDetailViewOpen(false);
                  setDetailViewInitialTab("basic");
                  setSelectedTarget(null);
                  onSelectedServiceIdsChange(
                    new Set(
                      Array.from(selectedServiceIds).filter((id) => id !== sid)
                    )
                  );
                  if (urlTargetId && onCloseDialog) {
                    onCloseDialog();
                  }
                } catch (err) {
                  showError(
                    `サービスの削除に失敗しました: ${
                      err instanceof Error ? err.message : "不明なエラー"
                    }`
                  );
                  throw err;
                }
              }
            : undefined
        }
      />
    </Container>
  );
};

export default TestTargetGraphView;
